import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import axios from 'axios';
import { TriangleAlert } from 'lucide-react';
import { Icon } from '../../components/Icon.tsx';
import { lettersApi, type Letter, type UpdateLetterInput } from './api.ts';
import { LetterBodyEditor } from './LetterBodyEditor.tsx';
import { AddressPicker, type AddressSnapshot } from '../address-book/AddressPicker.tsx';
import { dv } from '../../i18n/dv.ts';
import { useAuthStore } from '../../store/auth.ts';
import { displayName } from '../../lib/utils.ts';
import { isoToMvtDateInput, mvtDateInputToIso } from '../../lib/dvDate.ts';

const EMPTY_ADDRESS: AddressSnapshot = { officeName: '' };

// Render whatever a failed mutation gives us as a readable Dhivehi-ish
// string. Backend errors travel as `{ error: string }` (see
// backend/src/routes/letters.ts's handleError) — surface that verbatim when
// present, otherwise fall back to the generic error copy. Never swallow a
// mutation error silently (ecosystem "always render mutation errors" rule).
function mutationErrorText(e: unknown): string {
  if (axios.isAxiosError(e)) {
    const data = e.response?.data as { error?: string } | undefined;
    if (data?.error) return data.error;
  }
  return dv.common.error;
}

// Open a same-origin Blob as a temporary object URL, then revoke it once the
// browser has had a chance to actually load it (immediate revocation can race
// a newly opened tab / navigation on some browsers). Used for the letter PDF,
// which must be fetched with the Bearer token (see lettersApi.pdfBlob) rather
// than linked directly — a plain href/window.open to the API URL 401s.
function openBlobInNewTab(blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const win = window.open(url, '_blank', 'noopener');
  // If the popup was blocked, fall back to a download so the user still gets
  // the PDF instead of silently nothing happening.
  if (!win) downloadBlob(blob, 'letter.pdf');
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

// The editable field-column state. bodyHtml lives here too — Task 10 wires it
// to the TipTap rich-text editor (LetterBodyEditor) via the same shape and
// the same debounced autosave path the rest of the fields already use.
interface FormState {
  subject: string;
  letterDateInput: string; // <input type="date"> value, MVT calendar date
  letterNumber: string; // manual, user-typed — never auto-generated
  addressTo: AddressSnapshot;
  referringLetter: string;
  targetPages: number;
  signatoryName: string;
  signatoryDesignation: string;
  bodyHtml: string;
}

function toFormState(letter: Letter): FormState {
  return {
    subject: letter.subject,
    letterDateInput: isoToMvtDateInput(letter.letterDate),
    letterNumber: letter.letterNumber ?? '',
    addressTo: letter.addressTo ?? EMPTY_ADDRESS,
    referringLetter: letter.referringLetter ?? '',
    targetPages: letter.targetPages,
    signatoryName: letter.signatoryName,
    signatoryDesignation: letter.signatoryDesignation ?? '',
    bodyHtml: letter.bodyHtml,
  };
}

function toPatch(f: FormState): UpdateLetterInput {
  return {
    subject: f.subject,
    letterDate: mvtDateInputToIso(f.letterDateInput),
    letterNumber: f.letterNumber,
    addressTo: f.addressTo,
    referringLetter: f.referringLetter || undefined,
    targetPages: f.targetPages,
    signatoryName: f.signatoryName,
    signatoryDesignation: f.signatoryDesignation || undefined,
    bodyHtml: f.bodyHtml,
  };
}

const AUTOSAVE_DELAY_MS = 900;

// The field column + draft autosave, shared by both /letters/new (after the
// initial create) and /letters/:id. `letter` is the freshly-fetched row;
// `onSaved` lets the parent refresh its own query cache / status pill.
function EditorFields({ letter, onSaved }: { letter: Letter; onSaved: (l: Letter) => void }): JSX.Element {
  const user = useAuthStore((s) => s.user);
  // Init-once (the ecosystem auto-save sync-effect lesson): local state is
  // seeded from `letter` exactly once via useState's lazy initializer, NOT
  // from a useEffect([letter]) that would re-run and clobber in-progress
  // typing every time `onSaved` below swaps in the server's response after a
  // debounced PATCH. The caller additionally mounts this component with
  // `key={letter.id}` so a genuinely different letter (nav from one draft to
  // another) gets a fresh instance instead of silently reusing state.
  const [form, setForm] = useState<FormState>(() => toFormState(letter));
  const [savedState, setSavedState] = useState<'idle' | 'pending' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);

  const readOnly = letter.status === 'FINALIZED';

  const update = useMutation({
    mutationFn: (patch: UpdateLetterInput) => lettersApi.update(letter.id, patch),
    onSuccess: (updated) => { setSavedState('saved'); setSaveError(null); onSaved(updated); },
    // Autosave must never fail silently — leaving the "ސޭވްވަނީ…" spinner
    // stuck forever hides real data loss from the user (ecosystem "always
    // render mutation errors" rule).
    onError: (e) => { setSavedState('error'); setSaveError(mutationErrorText(e)); },
  });

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const set = (patch: Partial<FormState>) => {
    if (readOnly) return;
    setForm((f) => {
      const next = { ...f, ...patch };
      setSavedState('pending');
      setSaveError(null);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        update.mutate(toPatch(next));
      }, AUTOSAVE_DELAY_MS);
      return next;
    });
  };

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  // Prefill signatory name from the logged-in user on a brand-new, still-blank
  // letter only — never overwrite an already-set value.
  useEffect(() => {
    if (!form.signatoryName && user) {
      set({ signatoryName: displayName(user) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-6">
      <div role="status" aria-live="polite" className="flex min-h-5 items-center justify-end text-xs text-muted-foreground">
        {savedState === 'pending' && dv.letters.saving}
        {savedState === 'saved' && dv.letters.savedDraft}
        {savedState === 'error' && (
          <span className="text-destructive">{`${dv.letters.saveFailed}${saveError ? `: ${saveError}` : ''}`}</span>
        )}
      </div>

      <label className="block space-y-1 text-sm">
        <span className="field-label">{dv.letters.subject} *</span>
        <input
          className="input w-full"
          value={form.subject}
          onChange={(e) => set({ subject: e.target.value })}
          maxLength={500}
          disabled={readOnly}
          required
        />
      </label>

      <label className="block space-y-1 text-sm">
        <span className="field-label">{dv.letters.letterDate} *</span>
        <input
          type="date"
          className="input w-full"
          value={form.letterDateInput}
          onChange={(e) => set({ letterDateInput: e.target.value })}
          disabled={readOnly}
          required
        />
      </label>

      <label className="block space-y-1 text-sm">
        <span className="field-label">{dv.letters.letterNumber}</span>
        <input
          className="input w-full"
          value={form.letterNumber}
          onChange={(e) => set({ letterNumber: e.target.value })}
          maxLength={100}
          disabled={readOnly}
          dir="ltr"
        />
      </label>

      <div className="space-y-1 text-sm">
        <span className="field-label">{dv.letters.addressTo} *</span>
        <AddressPicker value={form.addressTo} onChange={(addressTo) => set({ addressTo })} />
      </div>

      <label className="block space-y-1 text-sm">
        <span className="field-label">{dv.letters.referringLetter}</span>
        <input
          className="input w-full"
          value={form.referringLetter}
          onChange={(e) => set({ referringLetter: e.target.value })}
          maxLength={500}
          disabled={readOnly}
        />
      </label>

      <label className="block space-y-1 text-sm">
        <span className="field-label">{dv.letters.targetPages}</span>
        <input
          type="number"
          min={1}
          max={5}
          className="input w-full"
          value={form.targetPages}
          onChange={(e) => set({ targetPages: Math.min(5, Math.max(1, Number(e.target.value) || 1)) })}
          disabled={readOnly}
        />
      </label>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block space-y-1 text-sm">
          <span className="field-label">{dv.letters.signatoryName} *</span>
          <input
            className="input w-full"
            value={form.signatoryName}
            onChange={(e) => set({ signatoryName: e.target.value })}
            maxLength={200}
            disabled={readOnly}
            required
          />
        </label>
        <label className="block space-y-1 text-sm">
          <span className="field-label">{dv.letters.signatoryDesignation}</span>
          <input
            className="input w-full"
            value={form.signatoryDesignation}
            onChange={(e) => set({ signatoryDesignation: e.target.value })}
            maxLength={200}
            disabled={readOnly}
          />
        </label>
      </div>

      <div className="space-y-1 text-sm">
        <span className="field-label">{dv.letters.body}</span>
        <LetterBodyEditor
          value={form.bodyHtml}
          onChange={(bodyHtml) => set({ bodyHtml })}
          readOnly={readOnly}
        />
      </div>
    </div>
  );
}

// /letters/new — creates a bare draft immediately (so there's an id to PATCH
// against), then navigates into the id'd route. Field defaults come straight
// from the schema's minimums so the create POST validates. A blank draft is
// a valid create (see backend createSchema) — only finalize enforces
// required fields — so this should always succeed; but if it doesn't (e.g.
// network/auth/server error), never leave the page stuck on the spinner
// forever: render the error and let the user retry or go back (ecosystem
// "always render mutation errors" rule).
function NewLetter(): JSX.Element {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const create = useMutation({
    mutationFn: () => lettersApi.create({
      subject: '',
      // Default to today (MVT). isoToMvtDateInput reads the current instant
      // through the same MVT wall-clock the field below writes back with.
      letterDate: mvtDateInputToIso(isoToMvtDateInput(new Date().toISOString())),
      addressTo: EMPTY_ADDRESS,
      signatoryName: user ? displayName(user) : '',
    }),
    onSuccess: (letter) => { void navigate({ to: '/letters/$id', params: { id: letter.id }, replace: true }); },
  });
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    create.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (create.isError) {
    return (
      <div className="page space-y-6">
        <div className="card-paper max-w-md space-y-4 p-5">
          <p className="text-sm text-destructive">{mutationErrorText(create.error)}</p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => { firedRef.current = true; create.mutate(); }}
            >
              {dv.common.retry}
            </button>
            <Link to="/letters" className="text-sm text-muted-foreground hover:text-foreground">
              {dv.letters.backToList}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page space-y-6">
      <p className="text-sm text-muted-foreground">{dv.common.loading}</p>
    </div>
  );
}

export function LetterEditor({ id }: { id?: string }): JSX.Element {
  if (!id) return <NewLetter />;
  return <ExistingLetter id={id} />;
}

const PREVIEW_POLL_MS = 1200;
const PREVIEW_POLL_TIMEOUT_MS = 30_000;

// Preview / finalize / reopen / download. The backend enforces status +
// permission rules server-side (draft-only preview/finalize, finalized-only
// reopen) — the UI mirrors that by only rendering the buttons that make
// sense for the current status, but the real gate is the API. Every mutation
// error is rendered inline (never swallowed), per the ecosystem rule.
function LetterActions({ letter, onUpdated }: { letter: Letter; onUpdated: (l: Letter) => void }): JSX.Element {
  const [previewing, setPreviewing] = useState(false);
  const [finalizeInfo, setFinalizeInfo] = useState<{ letterNumber: string; fitOk: boolean } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const pollAbortRef = useRef(false);

  useEffect(() => () => { pollAbortRef.current = true; }, []);

  const preview = useMutation({
    mutationFn: () => lettersApi.preview(letter.id),
    onMutate: () => setActionError(null),
    onSuccess: async () => {
      setPreviewing(true);
      pollAbortRef.current = false;
      const startedAt = Date.now();
      const beforePdfPath = letter.pdfPath;
      try {
        // The PDF worker renders async (POST /preview just enqueues the job —
        // see backend/src/routes/letters.ts). Poll the letter until pdfPath
        // changes (a fresh render landed), then open it. This runs inside
        // useMutation's onSuccess, not mutationFn, so a throw here (e.g. the
        // authed pdfBlob fetch failing) would NOT reach the mutation's
        // onError — catch and surface it explicitly instead of swallowing it.
        for (;;) {
          if (pollAbortRef.current) return;
          await new Promise((r) => setTimeout(r, PREVIEW_POLL_MS));
          const fresh = await lettersApi.get(letter.id);
          onUpdated(fresh);
          if (fresh.pdfPath && fresh.pdfPath !== beforePdfPath) {
            const blob = await lettersApi.pdfBlob(letter.id);
            openBlobInNewTab(blob);
            return;
          }
          if (Date.now() - startedAt > PREVIEW_POLL_TIMEOUT_MS) {
            setActionError(dv.common.error);
            return;
          }
        }
      } catch (e) {
        setActionError(mutationErrorText(e));
      } finally {
        setPreviewing(false);
      }
    },
    onError: (e) => setActionError(mutationErrorText(e)),
  });

  const finalize = useMutation({
    mutationFn: () => lettersApi.finalize(letter.id),
    onMutate: () => { setActionError(null); setFinalizeInfo(null); },
    onSuccess: (updated) => {
      onUpdated(updated);
      if (updated.letterNumber) {
        setFinalizeInfo({ letterNumber: updated.letterNumber, fitOk: updated.pdfFitOk !== false });
      }
    },
    onError: (e) => setActionError(mutationErrorText(e)),
  });

  const reopen = useMutation({
    mutationFn: () => lettersApi.reopen(letter.id),
    onMutate: () => setActionError(null),
    onSuccess: (updated) => { onUpdated(updated); setFinalizeInfo(null); },
    onError: (e) => setActionError(mutationErrorText(e)),
  });

  // Download must go through the authed axios instance (Bearer token) — a
  // plain <a href> to the API URL 401s (see lettersApi.pdfBlob).
  const download = useMutation({
    mutationFn: () => lettersApi.pdfBlob(letter.id),
    onMutate: () => setActionError(null),
    onSuccess: (blob) => { downloadBlob(blob, `${letter.letterNumber || letter.subject || 'letter'}.pdf`); },
    onError: (e) => setActionError(mutationErrorText(e)),
  });

  const isDraft = letter.status === 'DRAFT';
  const isFinalized = letter.status === 'FINALIZED';

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {isDraft && (
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={preview.isPending || previewing}
            onClick={() => preview.mutate()}
          >
            {previewing ? <Icon name="progress_activity" size={14} className="animate-spin" /> : <Icon name="visibility" size={14} />}
            {previewing ? dv.letters.previewing : dv.letters.preview}
          </button>
        )}

        {isDraft && (
          <button
            type="button"
            className="btn-primary btn-sm"
            disabled={finalize.isPending}
            onClick={() => { if (window.confirm(dv.letters.finalizeConfirm)) finalize.mutate(); }}
          >
            {finalize.isPending ? <Icon name="progress_activity" size={14} className="animate-spin" /> : <Icon name="send" size={14} />}
            {finalize.isPending ? dv.letters.finalizing : dv.letters.finalize}
          </button>
        )}

        {isFinalized && (
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={reopen.isPending}
            onClick={() => { if (window.confirm(dv.letters.reopenConfirm)) reopen.mutate(); }}
          >
            {reopen.isPending ? <Icon name="progress_activity" size={14} className="animate-spin" /> : <Icon name="refresh" size={14} />}
            {reopen.isPending ? dv.letters.reopening : dv.letters.reopen}
          </button>
        )}

        {letter.pdfPath ? (
          <button
            type="button"
            className="btn-ghost btn-sm"
            disabled={download.isPending}
            onClick={() => download.mutate()}
          >
            {download.isPending ? <Icon name="progress_activity" size={14} className="animate-spin" /> : <Icon name="download" size={14} />}
            {dv.letters.downloadPdf}
          </button>
        ) : (
          <span className="text-xs text-muted-foreground">{dv.letters.pdfNotReady}</span>
        )}
      </div>

      {finalizeInfo && (
        <div className="space-y-1">
          <p className="text-sm" style={{ color: 'hsl(var(--success))' }}>
            {dv.letters.finalizedNumber}: <span className="mono-meta">{finalizeInfo.letterNumber}</span>
          </p>
          {!finalizeInfo.fitOk && (
            <p className="flex items-center gap-1.5 text-sm text-destructive">
              <TriangleAlert size={14} />
              {dv.letters.fitNotOk}
            </p>
          )}
        </div>
      )}

      {actionError && <p className="text-sm text-destructive">{actionError}</p>}
    </div>
  );
}

function ExistingLetter({ id }: { id: string }): JSX.Element {
  const qc = useQueryClient();
  const { data: letter, isLoading } = useQuery({
    queryKey: ['letters', id],
    queryFn: () => lettersApi.get(id),
  });

  if (isLoading || !letter) {
    return (
      <div className="page space-y-6">
        <p className="text-sm text-muted-foreground">{dv.common.loading}</p>
      </div>
    );
  }

  return (
    <div className="page space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <Link
            to="/letters"
            className="btn-icon shrink-0"
            aria-label={dv.letters.backToList}
            title={dv.letters.backToList}
          >
            <Icon name="arrow_forward" size={20} />
          </Link>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            {letter.subject || dv.letters.newTitle}
          </h1>
          {letter.status === 'FINALIZED' ? (
            <span className="pill pill-success shrink-0">{dv.letters.finalizedPill}</span>
          ) : (
            <span className="pill pill-neutral shrink-0">{dv.letters.draftPill}</span>
          )}
        </div>
        <LetterActions
          key={`${letter.id}-actions`}
          letter={letter}
          onUpdated={(updated) => { qc.setQueryData(['letters', id], updated); }}
        />
      </header>

      <div className="card-paper max-w-4xl p-5 sm:p-8">
        <EditorFields
          key={letter.id}
          letter={letter}
          onSaved={(updated) => {
            qc.setQueryData(['letters', id], updated);
          }}
        />
      </div>
    </div>
  );
}
